/**
 * Pure helpers that compute y-axis ranges from visible timeseries data.
 *
 * @packageDocumentation
 * @module TimeseriesAutoscaleYRanges
 * @public
 */

import { upgradeAxisPreferences } from '../config/plotPreferences';
import {
  AxisKey,
  AxisPreferences,
  CurveId,
  PlotPreferences,
  SourceSlotIndex,
} from '../config/plotTypes';
import {
  CurveSessionVisibility,
  isCurveSessionVisible,
} from './curveSessionVisibility';
import { PlotDataInput } from './PlotDataInput';
import { EpochMillisecondRange } from './timeseriesAxisTicks';

// -- type definitions --

/** Input for visible-window y autoscale computation. */
export interface ComputeTimeseriesAutoscaleYRangesInput {
  readonly preferences: PlotPreferences;
  readonly data: PlotDataInput;
  readonly visibleXRangeMs: EpochMillisecondRange;
  readonly curveSessionVisibility?: CurveSessionVisibility;
}

// -- constants --

/** Padding applied above and below visible min and max y values. */
const Y_RANGE_PADDING_RATIO = 0.05;

/** Fallback padding when all visible y values are equal. */
const FLAT_Y_RANGE_PADDING = 1;

// -- helper functions --

/**
 * Returns whether an axis uses visible-window autoscale instead of fixed bounds.
 *
 * @param axis - Axis preferences to inspect.
 * @returns `true` when the axis is in autoscale mode.
 */
export function isAxisInAutoscaleMode(axis: AxisPreferences): boolean {
  return upgradeAxisPreferences(axis).useBounds !== true;
}

/**
 * Reads one curve value from a data row.
 *
 * @param preferences - Plot preferences containing source slot mapping.
 * @param row - One measurement row, when present.
 * @param curveId - Target curve id.
 * @returns Numeric value or `null` when missing.
 */
function readDataValueForCurve(
  preferences: PlotPreferences,
  row: Record<string, number> | undefined,
  curveId: CurveId
): number | null {
  if (!row) {
    return null;
  }

  const sourceSlotIndex: SourceSlotIndex | undefined =
    preferences.curveToSourceIndex?.[curveId];

  if (sourceSlotIndex !== undefined) {
    return row[`${sourceSlotIndex}`] ?? null;
  }

  return row[curveId] ?? null;
}

/**
 * Returns whether a timestamp falls inside the visible x-axis window.
 *
 * @param instant - Measurement timestamp.
 * @param visibleXRangeMs - Visible x-axis range in epoch milliseconds.
 * @returns `true` when the point is visible on the x-axis.
 */
function isInstantInsideVisibleXRange(
  instant: Date,
  visibleXRangeMs: EpochMillisecondRange
): boolean {
  const epochMs = instant.getTime();
  return epochMs >= visibleXRangeMs[0] && epochMs <= visibleXRangeMs[1];
}

/**
 * Adds padding around a min/max pair so flat lines remain readable.
 *
 * @param minimumY - Smallest visible y value.
 * @param maximumY - Largest visible y value.
 * @returns Padded axis range.
 */
export function padVisibleYRange(
  minimumY: number,
  maximumY: number
): [number, number] {
  if (minimumY === maximumY) {
    const padding =
      Math.abs(minimumY) > 0
        ? Math.abs(minimumY) * Y_RANGE_PADDING_RATIO
        : FLAT_Y_RANGE_PADDING;

    return [minimumY - padding, maximumY + padding];
  }

  const span = maximumY - minimumY;
  const padding = span * Y_RANGE_PADDING_RATIO;

  return [minimumY - padding, maximumY + padding];
}

/**
 * Collects curve ids that contribute to one y-axis range.
 *
 * Interval relation min and max curves are included when they share the axis.
 *
 * @param preferences - Plot preferences.
 * @param axisKey - Target y-axis key.
 * @returns Curve ids whose values should affect the axis range.
 */
function collectCurveIdsForAxis(
  preferences: PlotPreferences,
  axisKey: AxisKey
): readonly CurveId[] {
  const curveIds = new Set<CurveId>();

  (Object.entries(preferences.dataSourceToAxis) as [CurveId, AxisKey][]).forEach(
    ([curveId, mappedAxisKey]) => {
      if (mappedAxisKey === axisKey) {
        curveIds.add(curveId);
      }
    }
  );

  for (const relation of preferences.curveRelations ?? []) {
    if (relation.relationType !== 'interval') {
      continue;
    }

    const mainAxisKey = preferences.dataSourceToAxis[relation.mainCurve];
    if (mainAxisKey !== axisKey) {
      continue;
    }

    relation.relatedCurves.forEach((curveId) => {
      if (preferences.dataSourceToAxis[curveId] === axisKey) {
        curveIds.add(curveId);
      }
    });
  }

  return [...curveIds];
}

/**
 * Collects visible numeric y values for one axis inside the x-axis window.
 *
 * @param input - Autoscale computation input.
 * @param axisKey - Target y-axis key.
 * @returns Visible y values for the axis.
 */
function collectVisibleYValuesForAxis(
  input: ComputeTimeseriesAutoscaleYRangesInput,
  axisKey: AxisKey
): readonly number[] {
  const { preferences, data, visibleXRangeMs, curveSessionVisibility } = input;
  const values: number[] = [];

  for (const curveId of collectCurveIdsForAxis(preferences, axisKey)) {
    if (
      !isCurveSessionVisible(curveId, preferences, curveSessionVisibility)
    ) {
      continue;
    }

    for (const [instant, row] of data.entries()) {
      if (!isInstantInsideVisibleXRange(instant, visibleXRangeMs)) {
        continue;
      }

      const value = readDataValueForCurve(preferences, row, curveId);
      if (value !== null && value !== undefined && !Number.isNaN(value)) {
        values.push(value);
      }
    }
  }

  return values;
}

// -- main --

/**
 * Computes y-axis ranges from visible data for autoscale axes in timeseries mode.
 *
 * Axes with fixed bounds (`useBounds: true`) are omitted from the result.
 *
 * @param input - Plot data, preferences, and visible x-axis range.
 * @returns Autoscale y ranges keyed by axis key.
 */
export function computeTimeseriesAutoscaleYRanges(
  input: ComputeTimeseriesAutoscaleYRangesInput
): Partial<Record<AxisKey, [number, number]>> {
  const { preferences } = input;
  const ranges: Partial<Record<AxisKey, [number, number]>> = {};

  for (const [axisKey, axisPreferences] of Object.entries(
    preferences.axisPrefs
  ) as [AxisKey, AxisPreferences][]) {
    if (axisPreferences.isXAxis || !isAxisInAutoscaleMode(axisPreferences)) {
      continue;
    }

    const visibleValues = collectVisibleYValuesForAxis(input, axisKey);
    if (visibleValues.length === 0) {
      continue;
    }

    const minimumY = Math.min(...visibleValues);
    const maximumY = Math.max(...visibleValues);
    ranges[axisKey] = padVisibleYRange(minimumY, maximumY);
  }

  return ranges;
}
